/**
 * UG Renewal Video Carousel - Web Component
 * 使用 Swiper 插件，一屏 2.4 个；左右箭头 <3 项时隐藏；点击卡片弹窗播放。
 */
(function () {
  const MIN_ITEMS_FOR_ARROWS = 3;

  class UgRenewalVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this._swiperEl = null;
      this._swiper = null;
      this._modal = null;
      this._slot = null;
      this._nav = null;
      this._prev = null;
      this._next = null;
      this._items = [];
      this._boundKeydown = this._onKeydown.bind(this);
      this._boundResize = this._onResize.bind(this);
      this._videoTriggerHandlers = [];
      this._boundModalClick = null;
      this._swiperInitTimer = null;
    }

    connectedCallback() {
      this._swiperEl = this.querySelector('[data-swiper-container]');
      this._modal = this.querySelector('[data-video-modal]');
      this._slot = this.querySelector('[data-video-slot]');
      this._nav = this.querySelector('[data-carousel-nav]');
      this._prev = this.querySelector('[data-carousel-prev]');
      this._next = this.querySelector('[data-carousel-next]');
      this._items = Array.from(this.querySelectorAll('[data-carousel-item]'));

      if (!this._swiperEl) return;

      this._updateNavVisibility();
      this._initSwiper();
      this._setupVideoTriggers();
      this._setupModalClose();
      window.addEventListener('resize', this._boundResize);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._boundKeydown);
      if (this._modal && !this._modal.hidden) {
        this._closeModal();
      } else {
        document.body.style.overflow = '';
      }
      if (this._swiper && typeof this._swiper.destroy === 'function') {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
      if (this._swiperInitTimer) {
        clearInterval(this._swiperInitTimer);
        this._swiperInitTimer = null;
      }
      window.removeEventListener('resize', this._boundResize);
      if (this._videoTriggerHandlers && this._videoTriggerHandlers.length) {
        this._videoTriggerHandlers.forEach(({ el, fn }) => { try { el.removeEventListener('click', fn); } catch (e) {} });
        this._videoTriggerHandlers = [];
      }
      if (this._modal && this._boundModalClick) {
        try { this._modal.removeEventListener('click', this._boundModalClick); } catch (e) {}
        this._boundModalClick = null;
      }
    }

    _syncPosition() {
      if (!this._swiper) return;
      this._updateNavState();
      this._updateNavVisibility();
    }

    _updateNavVisibility() {
      if (!this._nav) return;
      if (this._items.length < MIN_ITEMS_FOR_ARROWS) {
        this._nav.classList.add('is-hidden');
        return;
      }
      if (this._swiper && this._swiper.isLocked) {
        this._nav.classList.add('is-hidden');
      } else {
        this._nav.classList.remove('is-hidden');
      }
    }

    _initSwiper() {
      if (typeof window.Swiper === 'undefined') {
        if (!this._swiperInitTimer) {
          this._swiperInitTimer = setInterval(() => {
            if (typeof window.Swiper !== 'undefined') {
              clearInterval(this._swiperInitTimer);
              this._swiperInitTimer = null;
              this._initSwiper();
            }
          }, 100);
        }
        return;
      }
      var self = this;
      this._swiper = new window.Swiper(self._swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        slidesPerGroup: 1,
        allowTouchMove: true,
        observer: true,
        observeParents: true,
        watchOverflow: true,
        breakpoints: {
          320: { spaceBetween: 12 },
          768: { spaceBetween: 16 }
        },
        resizeObserver: true,
        navigation: {
          nextEl: self._next,
          prevEl: self._prev
        },
        on: {
          init: function () {
            self._swiper = this;
            self._updateNavState();
            self._updateNavVisibility();
            self._applyOffsets();
          },
          slideChange: function () {
            self._updateNavState();
            self._updateNavVisibility();
          },
          slideChangeTransitionEnd: function () {
            self._updateNavState();
            self._updateNavVisibility();
          },
          resize: function () {
            self._updateNavState();
            self._updateNavVisibility();
          }
        }
      });
    }

    _updateNavState() {
      if (!this._swiper || !this._prev || !this._next) return;
      if (this._items.length <= 1) {
        this._prev.classList.add('is-disabled');
        this._prev.setAttribute('disabled', '');
        this._prev.setAttribute('aria-disabled', 'true');
        this._next.classList.add('is-disabled');
        this._next.setAttribute('disabled', '');
        this._next.setAttribute('aria-disabled', 'true');
        return;
      }
      if (this._swiper.isBeginning) {
        this._prev.classList.add('is-disabled');
        this._prev.setAttribute('disabled', '');
        this._prev.setAttribute('aria-disabled', 'true');
      } else {
        this._prev.classList.remove('is-disabled');
        this._prev.removeAttribute('disabled');
        this._prev.removeAttribute('aria-disabled');
      }
      if (this._swiper.isEnd) {
        this._next.classList.add('is-disabled');
        this._next.setAttribute('disabled', '');
        this._next.setAttribute('aria-disabled', 'true');
      } else {
        this._next.classList.remove('is-disabled');
        this._next.removeAttribute('disabled');
        this._next.removeAttribute('aria-disabled');
      }
    }

    _onResize() {
      this._syncPosition();
      this._applyOffsets();
    }

    _applyOffsets() {
      if (!this._swiperEl || !this._swiper) return;
      var wrapper = this._swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) return;
      var styles = window.getComputedStyle(wrapper);
      var leftPad = parseFloat(styles.paddingLeft || '0') || 0;
      var rightPad = parseFloat(styles.paddingRight || '0') || 0;
      var offsetAfter = Math.max(leftPad, rightPad);
      this._swiper.params.slidesOffsetAfter = offsetAfter;
      this._swiper.update();
      this._updateNavState();
      this._updateNavVisibility();
    }

    _setupVideoTriggers() {
      const self = this;
      this._videoTriggerHandlers = [];
      this.querySelectorAll('[data-video-trigger]').forEach((btn) => {
        const handler = function (e) {
          e.preventDefault();
          const item = this.closest('[data-carousel-item]');
          if (!item || !self._modal || !self._slot) return;
          const type = (item.getAttribute('data-video-type') || 'youtube').toLowerCase();
          const url = item.getAttribute('data-video-url') || '';
          if (!url) return;
          self._openModal(type, url);
        };
        btn.addEventListener('click', handler);
        self._videoTriggerHandlers.push({ el: btn, fn: handler });
      });
    }

    _openModal(videoType, url) {
      if (!this._modal || !this._slot) return;

      this._slot.innerHTML = '';

      if (videoType === 'youtube') {
        const iframe = document.createElement('iframe');
        iframe.src = url;
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        iframe.allowFullscreen = true;
        this._slot.appendChild(iframe);
      } else {
        const video = document.createElement('video');
        video.src = url;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        this._slot.appendChild(video);
      }

      this._modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._boundKeydown);
    }

    _closeModal() {
      if (!this._modal || !this._slot) return;

      const iframe = this._slot.querySelector('iframe');
      const video = this._slot.querySelector('video');
      if (iframe) {
        iframe.removeAttribute('src');
      }
      if (video) {
        video.pause();
        video.removeAttribute('src');
      }
      this._slot.innerHTML = '';

      this._modal.hidden = true;
      this._modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._boundKeydown);
    }

    _onKeydown(e) {
      if (e.key === 'Escape' && this._modal && !this._modal.hidden) {
        this._closeModal();
      }
    }

    _setupModalClose() {
      if (!this._modal) return;

      const self = this;
      this._boundModalClick = function (e) {
        if (e.target.matches('[data-modal-close]') || e.target.closest('[data-modal-close]')) {
          self._closeModal();
          return;
        }
        const backdrop = self._modal.querySelector('.ug-renewal-video-carousel__modal-backdrop');
        if (e.target === backdrop) {
          self._closeModal();
        }
      };
      this._modal.addEventListener('click', this._boundModalClick);
    }
  }

  if (!customElements.get('ug-renewal-video-carousel')) {
    customElements.define('ug-renewal-video-carousel', UgRenewalVideoCarousel);
  }
})();
